import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import isEqual from 'lodash/isEqual';
import './score-board.scss';
import classNames from 'classnames';
import { create, nextRepaint } from '~shared/utils/dom';

/**
 * Holds the strategies for each sport slug
 * @type {Object}
 */
import NormalizeStrategies from './normalizers';

/**
 * Creates the score column element
 * @param  {Array} columnFields The fields for the score column
 * @param  {String} sport The sport slug
 * @param  {Bool} isUsFormat SHould display in us format
 * @return {Element} The score column element
 */
function createScoreColumn(columnFields, sport, isUsFormat) {
    const scoreColumn = create('div', `bb-score-board__score-column bb-score-board__score-column--${sport}`);

    if (isUsFormat) {
        columnFields.reverse();
    }

    columnFields.forEach(({value, bemModifiers}) => {
        const modifiers = bemModifiers.map(modifier => `bb-score-board__score-field--${modifier}`);
        scoreColumn.appendChild(create('span', classNames('bb-score-board__score-field', modifiers), value));
    });

    return scoreColumn;
}

/**
 * Checks if the score board should be updated
 * @param  {Object} state The event object
 * @return {Boolean} Returns true if the state has extra_info or false if not
 */
function shouldUpdate(state) {
    return !isUndefined(state['extra_info']);
}

/**
 * Checks if the score should be updated
 * @param  {Object} nextState The normalized event object
 * @param  {Object} currentState The current normalized event object
 * @return {Boolean} Returns true if the score has changed or false if not
 */
function shouldUpdateScore(nextState, currentState) {
    if (isNull(currentState)) {
        return true;
    }

    return !isEqual(nextState.columns, currentState.columns);
}

/**
 * Checks if the status should be updated
 * @param  {Object} nextState The normalized event object
 * @param  {Object} currentState The current normalized event object
 * @return {Boolean} Returns true if the status has changed or false if not
 */
function shouldUpdateStatus(nextState, currentState) {
    if (isNull(currentState)) {
        return true;
    }

    return nextState.status !== currentState.status;
}

/**
 * Creates a score board
 * @param  {String} sport The sport slug
 * @param  {Object} initialState The event data
 * @param  {Bool} shouldShowStreaming should display streaming icon if streaming is available
 * @return {Null|ScoreBoard} The score board API or null if it cannot create it
 */
export default function createScoreBoard(sport, initialState, shouldShowStreaming = false) {
    const {
        usesThreeMarketView = false,
        isUsFormat = false,
    } = initialState;

    if (isUndefined(NormalizeStrategies[sport])) {
        return null;
    }

    /**
     * The component dom element
     * @type {Element}
     */
    const el = create('div', classNames(`bb-score-board bb-score-board--${sport}`, {
        ['bb-score-board--three_market_view']: usesThreeMarketView,
        ['bb-score-board--show-streaming']: shouldShowStreaming,
    }));

    /**
     * The columns wrapper dom element
     * @type {Element}
     */
    const columns = create('div', `bb-score-board__score-columns bb-score-board__score-columns--${sport}`);

    /**
     * The match status dom element
     * @type {Element}
     */
    const matchStatus = create('span', `bb-score-board__match-status bb-score-board__match-status--${sport}`);

    /**
     * The current score board state
     * @type {Object}
     */
    let currentState = null;

    /**
     * The normalizer for the sport scores
     * @type {Function}
     */
    const normalize = NormalizeStrategies[sport];

    el.appendChild(columns);

    el.appendChild(matchStatus);

    update(initialState);

    /**
     * Updates the score board
     * @param  {Object} nextEventState The event data
     * @return {void}
     */
    function update(nextEventState) {

        if (shouldUpdate(nextEventState)) {
            const nextState = normalize(nextEventState, currentState);

            if (shouldUpdateScore(nextState, currentState)) {
                nextRepaint(() => {
                    while (columns.firstChild) {
                        columns.removeChild(columns.firstChild);
                    }

                    nextState.columns.forEach((column) => columns.appendChild(createScoreColumn(column, sport, isUsFormat)));
                });
            }

            if (shouldUpdateStatus(nextState, currentState)) {
                nextRepaint(() => {
                    matchStatus.innerHTML = nextState.status;
                });
            }

            currentState = {...nextState};
        }

    }

    /**
     * Adds the open bets modifier class name
     * @return {void}
     */
    function addOpenBetsModifier() {
        el.classList.add('bb-score-board--open-bets');
        matchStatus.classList.add('bb-score-board__match-status--open-bets');
    }

    /**
     * Removes the open bets modifier class name
     * @return {void}
     */
    function removeOpenBetsModifier() {
        el.classList.remove('bb-score-board--open-bets');
        matchStatus.classList.remove('bb-score-board__match-status--open-bets');
    }

    /**
     * ScoreBoard API
     */
    return {
        el,
        matchStatus,
        update,
        addOpenBetsModifier,
        removeOpenBetsModifier,
    };
}
